import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Archive, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { ProgramGovernmentOrgFields } from '../_shared/components/GovernmentOrgPicker';
import { PRICING_TYPE_OPTIONS } from '../_shared/constants/pricingTypes';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { businessUnitHooks, portfolioHooks } from '../Org/hooks';
import { useUpdateProgram } from './hooks';
import { ProgramCompanyFields } from './ProgramCompanyFields';
import { patchProgram } from './programPatch';
import { tenantEnumSelectOptions, useTenantEnumValues } from './referenceDataHooks';
export function ProgramEditDialog({ open, onOpenChange, program, readOnly, onDelete, onPurge }) {
    const update = useUpdateProgram();
    const operationalStates = useTenantEnumValues('operational_state');
    const recordStatuses = useTenantEnumValues('record_status');
    const contractLifecycleStates = useTenantEnumValues('contract_lifecycle_state');
    const deliveryModelTypes = useTenantEnumValues('delivery_model_type');
    const tenantId = useTenantId();
    const businessUnits = businessUnitHooks.useList({
        companyId: tenantId
    });
    const portfolios = portfolioHooks.useList({
        companyId: tenantId
    });
    const [governmentOrganizationId, setGovernmentOrganizationId] = useState(program.governmentOrganizationId ?? '');
    const [customerOrgId, setCustomerOrgId] = useState(program.customerOrgId ?? '');
    const [companyRoleType, setCompanyRoleType] = useState(program.companyRoleType ?? '');
    const [primeContractorCompanyId, setPrimeContractorCompanyId] = useState(program.primeContractorCompanyId ?? '');
    useEffect(()=>{
        if (open) {
            setGovernmentOrganizationId(program.governmentOrganizationId ?? '');
            setCustomerOrgId(program.customerOrgId ?? '');
            setCompanyRoleType(program.companyRoleType ?? '');
            setPrimeContractorCompanyId(program.primeContractorCompanyId ?? '');
        }
    }, [
        open,
        program
    ]);
    const fields = [
        {
            kind: 'text',
            name: 'programNameFull',
            label: 'Program name',
            required: true,
            maxLength: 300
        },
        {
            kind: 'text',
            name: 'programNameShort',
            label: 'Short name',
            maxLength: 100
        },
        {
            kind: 'text',
            name: 'contractNumber',
            label: 'Contract number',
            maxLength: 100
        },
        {
            kind: 'text',
            name: 'contractTitle',
            label: 'Contract title',
            maxLength: 300
        },
        {
            kind: 'select',
            name: 'pricingType',
            label: 'Pricing type',
            options: [
                ...PRICING_TYPE_OPTIONS
            ]
        },
        {
            kind: 'select',
            name: 'operationalStateId',
            label: 'Operational state',
            options: tenantEnumSelectOptions(operationalStates.data)
        },
        {
            kind: 'select',
            name: 'recordStatusId',
            label: 'Record status',
            options: tenantEnumSelectOptions(recordStatuses.data)
        },
        {
            kind: 'select',
            name: 'contractLifecycleStateId',
            label: 'Contract lifecycle',
            options: tenantEnumSelectOptions(contractLifecycleStates.data)
        },
        {
            kind: 'select',
            name: 'deliveryModelTypeId',
            label: 'Delivery model',
            options: tenantEnumSelectOptions(deliveryModelTypes.data)
        },
        {
            // SAD-75: this is what scopes the program's Team & Access role picker
            // and its Add-to-Team contact picker to a business unit. Empty means
            // unscoped — those pickers then fall back to the full, company-wide
            // lists, exactly as they did before this field existed.
            kind: 'select',
            name: 'businessUnitId',
            label: 'Business unit',
            testId: Testids.deliveryPrograms.businessUnitSelect,
            options: (businessUnits.data?.items ?? []).map((bu)=>({
                    value: bu.id,
                    label: bu.name
                }))
        },
        {
            // Org scoping: assigning a unit re-parents the program's access scope —
            // a unit-level grant (Delivery Unit Manager) then covers this program
            // automatically, including via future unit grants.
            kind: 'select',
            name: 'portfolioId',
            label: 'Organizational unit (portfolio)',
            options: (portfolios.data?.items ?? []).map((pf)=>({
                    value: pf.id,
                    label: pf.name
                }))
        }
    ];
    const handleSubmit = async (values)=>{
        const next = patchProgram(program, {
            programNameFull: values.programNameFull,
            programNameShort: values.programNameShort ?? '',
            contractNumber: values.contractNumber ?? '',
            contractTitle: values.contractTitle ?? '',
            pricingType: values.pricingType ?? '',
            operationalStateId: values.operationalStateId ?? '',
            recordStatusId: values.recordStatusId ?? '',
            contractLifecycleStateId: values.contractLifecycleStateId ?? '',
            deliveryModelTypeId: values.deliveryModelTypeId ?? '',
            businessUnitId: values.businessUnitId ?? '',
            portfolioId: values.portfolioId ?? '',
            governmentOrganizationId,
            customerOrgId,
            companyRoleType,
            primeContractorCompanyId
        });
        await update.mutateAsync({
            id: program.id,
            program: next
        });
        onOpenChange(false);
    };
    const operationalStateId = program.operationalStateId ?? '';
    return /*#__PURE__*/ _jsx(EntityFormDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: "Edit Program",
        description: "Contracting and customer offices can differ (e.g. TAC vs VR&E on RES).",
        fields: fields,
        defaultValues: {
            programNameFull: program.programNameFull,
            programNameShort: program.programNameShort,
            contractNumber: program.contractNumber,
            contractTitle: program.contractTitle,
            pricingType: program.pricingType ?? '',
            operationalStateId,
            recordStatusId: program.recordStatusId ?? '',
            contractLifecycleStateId: program.contractLifecycleStateId ?? '',
            deliveryModelTypeId: program.deliveryModelTypeId ?? '',
            businessUnitId: program.businessUnitId ?? '',
            portfolioId: program.portfolioId ?? ''
        },
        extraContent: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(ProgramGovernmentOrgFields, {
                    governmentOrganizationId: governmentOrganizationId,
                    customerOrgId: customerOrgId,
                    onGovernmentOrganizationIdChange: setGovernmentOrganizationId,
                    onCustomerOrgIdChange: setCustomerOrgId
                }),
                /*#__PURE__*/ _jsx(ProgramCompanyFields, {
                    companyRoleType: companyRoleType,
                    primeContractorCompanyId: primeContractorCompanyId,
                    onCompanyRoleTypeChange: setCompanyRoleType,
                    onPrimeContractorCompanyIdChange: setPrimeContractorCompanyId,
                    canEdit: !readOnly
                }),
                !readOnly && (onDelete || onPurge) ? /*#__PURE__*/ _jsxs("div", {
                    className: "mt-2 space-y-3 border-t border-border pt-4",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "text-[11px] font-medium uppercase tracking-[0.07em] text-muted-foreground",
                            children: "Danger zone"
                        }),
                        onDelete ? /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between gap-3",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs text-muted-foreground",
                                    children: "Archiving hides the program from Delivery. Its data is kept, and an admin can still purge it."
                                }),
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    className: "shrink-0",
                                    "data-testid": Testids.deliveryPrograms.deleteButton,
                                    onClick: onDelete,
                                    size: "sm",
                                    type: "button",
                                    variant: "destructive",
                                    children: [
                                        /*#__PURE__*/ _jsx(Archive, {
                                            className: "mr-1.5 h-3.5 w-3.5"
                                        }),
                                        "Archive"
                                    ]
                                })
                            ]
                        }) : null,
                        onPurge ? /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-xs text-muted-foreground",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium text-destructive",
                                            children: "Admin only."
                                        }),
                                        " Purging permanently destroys the program and every record attached to it. It cannot be undone."
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    className: "shrink-0",
                                    "data-testid": Testids.deliveryPrograms.purgeButton,
                                    onClick: onPurge,
                                    size: "sm",
                                    type: "button",
                                    variant: "danger",
                                    children: [
                                        /*#__PURE__*/ _jsx(Trash2, {
                                            className: "mr-1.5 h-3.5 w-3.5"
                                        }),
                                        "Purge permanently"
                                    ]
                                })
                            ]
                        }) : null
                    ]
                }) : null
            ]
        }),
        onSubmit: handleSubmit,
        isSubmitting: update.isPending,
        submitLabel: "Save",
        readOnly: readOnly
    });
}
